import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Kanban } from '~/components/Kanban/Kanban';
import { LeadsViewToggle } from '~/components/LeadsViewToggle/LeadsViewToggle';
import { LeadDetailPanel } from '~/components/LeadDetailPanel/LeadDetailPanel';
import { LostReasonDialog } from '~/components/LostReasonDialog/LostReasonDialog';
import { NewLeadModal } from '~/components/NewLeadModal/NewLeadModal';
import { PeriodFilter } from '~/components/PeriodFilter/PeriodFilter';
import { usePipelines, usePipelineStages, useLeads, useMoveLead } from '~/lib/queries';
import { formatPbError } from '~/lib/pb-error';
import { useOportunidadesPeriod, resolvePeriod, tenantTimeZone } from '~/lib/dashboard-period';
import { useActiveTenantId, useTenantContext } from '~/lib/tenantContext';
import { tenantCurrency } from '~/lib/capabilities';
import type { Lead } from '~/lib/types';
import styles from './_authed.crm.module.css';

export interface CrmSearch {
  pipeline?: string;
  stage?: string;
  vista?: 'actual';
}

export const Route = createFileRoute('/_authed/crm')({
  validateSearch: (search: Record<string, unknown>): CrmSearch => {
    const pipeline =
      typeof search.pipeline === 'string' && search.pipeline ? search.pipeline : undefined;
    const stage = typeof search.stage === 'string' && search.stage ? search.stage : undefined;
    const vista = search.vista === 'actual' ? 'actual' : undefined;
    return { pipeline, stage, vista };
  },
  component: CRMPage,
});

const PIPELINE_STORAGE_KEY = 'focus_active_pipeline_by_tenant';

function readPipelinePref(tenantId: string | null): string | null {
  if (!tenantId) return null;
  try {
    const map = JSON.parse(localStorage.getItem(PIPELINE_STORAGE_KEY) || '{}') as Record<
      string,
      string
    >;
    return map[tenantId] ?? null;
  } catch {
    return null;
  }
}

function writePipelinePref(tenantId: string, pipelineId: string) {
  try {
    const map = JSON.parse(localStorage.getItem(PIPELINE_STORAGE_KEY) || '{}') as Record<
      string,
      string
    >;
    map[tenantId] = pipelineId;
    localStorage.setItem(PIPELINE_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / SSR issues
  }
}

function CRMPage() {
  const tenantId = useActiveTenantId();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: pipelines, isLoading: loadingPipelines } = usePipelines();

  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const snapshot = Boolean(search.stage) || search.vista === 'actual';

  // Cuando cambia tenant o llegan los pipelines, resolver cuál mostrar:
  // 1. el pipeline de la URL (viene del dashboard)
  // 2. el guardado en localStorage (si existe y todavía es válido)
  // 3. el is_default
  // 4. el primero
  useEffect(() => {
    if (!pipelines || pipelines.length === 0) {
      setPipelineId(null);
      return;
    }
    const fromUrl = search.pipeline && pipelines.find((p) => p.id === search.pipeline);
    const stored = readPipelinePref(tenantId);
    const pickFromStored = stored && pipelines.find((p) => p.id === stored);
    const picked = fromUrl || pickFromStored || pipelines.find((p) => p.is_default) || pipelines[0];
    setPipelineId(picked?.id ?? null);
    if (fromUrl && tenantId) writePipelinePref(tenantId, fromUrl.id);
  }, [pipelines, tenantId, search.pipeline]);

  function handlePipelineChange(id: string) {
    setPipelineId(id);
    if (tenantId) writePipelinePref(tenantId, id);
    if (search.pipeline || search.stage || search.vista) {
      navigate({ search: {}, replace: true });
    }
  }

  const { selectedTenant } = useTenantContext();
  const [period, setPeriod] = useOportunidadesPeriod();
  const timeZone = tenantTimeZone(selectedTenant);
  const resolved = useMemo(() => resolvePeriod(period, { timeZone }), [period, timeZone]);
  const {
    data: stages,
    isLoading: loadingStages,
    isError: errorStages,
  } = usePipelineStages(pipelineId ?? undefined);
  const {
    data: leads,
    isLoading: loadingLeads,
    isError: errorLeads,
  } = useLeads(
    pipelineId ?? undefined,
    snapshot ? undefined : { createdFrom: resolved.start, createdTo: resolved.end },
  );
  const moveLead = useMoveLead();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showNewLead, setShowNewLead] = useState<boolean>(false);
  const [pendingLostMove, setPendingLostMove] = useState<{
    leadId: string;
    stageId: string;
    position: number;
    stageName: string;
    currentReason: string;
  } | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  function handleLeadMove(leadId: string, stageId: string, position: number) {
    const lead = leads?.find((l) => l.id === leadId);
    const fromStage = (stages ?? []).find((s) => s.id === lead?.stage);
    const toStage = (stages ?? []).find((s) => s.id === stageId);

    // Entrar a perdida → pedir razón antes de mover.
    if (toStage?.stage_type === 'lost' && fromStage?.stage_type !== 'lost') {
      setPendingLostMove({
        leadId,
        stageId,
        position,
        stageName: toStage.name,
        currentReason: lead?.lost_reason ?? '',
      });
      return;
    }

    const onError = (err: Error) => {
      console.error('[crm] move lead failed', err);
      setMoveError(formatPbError(err, 'No se pudo mover la oportunidad. Intenta de nuevo.'));
      window.setTimeout(() => setMoveError(null), 5000);
    };

    // Salir de perdida → limpiar razón.
    if (fromStage?.stage_type === 'lost' && toStage?.stage_type !== 'lost') {
      moveLead.mutate({ leadId, stageId, position, lost_reason: '' }, { onError });
      return;
    }

    moveLead.mutate({ leadId, stageId, position }, { onError });
  }

  if (loadingPipelines) {
    return (
      <div className={styles.crm}>
        <header className={styles.crm__header}>
          <h1 className="h2">Oportunidades</h1>
        </header>
        <div className="empty-state">Cargando…</div>
      </div>
    );
  }

  if (!pipelines || pipelines.length === 0) {
    return (
      <div className={styles.crm}>
        <header className={styles.crm__header}>
          <h1 className="h2">Oportunidades</h1>
        </header>
        <div className="empty-state">Aún no hay pipelines en este cliente.</div>
      </div>
    );
  }

  const activePipeline = pipelines.find((p) => p.id === pipelineId);
  const focusedStage = (stages ?? []).find((stage) => stage.id === search.stage);
  const totalLeads = leads?.length ?? 0;
  const totalValue = leads?.reduce((sum, l) => sum + (l.value ?? 0), 0) ?? 0;
  const scopeLabel = focusedStage?.name ?? (snapshot ? 'ahora' : resolved.longLabel);

  return (
    <div className={styles.crm}>
      <header className={styles.crm__header}>
        <div className={styles.crm__heading}>
          <h1 className={`h2 ${styles.crm__title}`}>Oportunidades</h1>
          <div className={styles.crm__pipelinePicker}>
            {pipelines.length === 1 ? (
              <p className={styles.crm__pipelineChip}>{activePipeline?.name}</p>
            ) : (
              <label className={styles.pipelineSelectWrap}>
                <span className={styles.crm__pipelineChip}>
                  {activePipeline?.name}
                  <ChevronDown size={14} className={styles.pipelineSelectChevron} aria-hidden />
                </span>
                <select
                  className={styles.pipelineSelectNative}
                  value={pipelineId ?? ''}
                  onChange={(e) => handlePipelineChange(e.target.value)}
                  aria-label="Cambiar pipeline"
                >
                  {pipelines.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.is_default ? ' (principal)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
        <div className={styles.crm__stats}>
          <div className={styles.crm__period}>
            {snapshot ? (
              <button
                type="button"
                className={styles.scopeChip}
                onClick={() => navigate({ search: {} })}
                aria-label={
                  focusedStage
                    ? `Quitar filtro de la etapa ${focusedStage.name}`
                    : 'Volver al período'
                }
              >
                {focusedStage?.name ?? 'Estado actual'}
                <span aria-hidden>×</span>
              </button>
            ) : (
              <PeriodFilter value={period} onChange={setPeriod} />
            )}
          </div>
          <div className={styles.crm__toggle}>
            <LeadsViewToggle active="kanban" />
          </div>
          <div className={styles.crm__stat}>
            <span className={styles.crm__statValue}>{totalLeads}</span>
            <span className={styles.crm__statLabel}>{scopeLabel}</span>
          </div>
          <div className={styles.crm__stat}>
            <span className={styles.crm__statValue}>
              {new Intl.NumberFormat('es-CL', {
                style: 'currency',
                currency: tenantCurrency(selectedTenant),
                maximumFractionDigits: 0,
              }).format(totalValue)}
            </span>
            <span className={styles.crm__statLabel}>valor</span>
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setShowNewLead(true)}>
            <Plus size={14} aria-hidden /> Nueva oportunidad
          </button>
        </div>
      </header>

      {showNewLead && (
        <NewLeadModal onClose={() => setShowNewLead(false)} pipelineId={pipelineId ?? undefined} />
      )}

      {moveError && (
        <div className={styles.crm__moveError} role="alert">
          {moveError}
        </div>
      )}

      {loadingStages || loadingLeads ? (
        <div className="empty-state">Cargando pipeline…</div>
      ) : errorStages || errorLeads ? (
        <div className="empty-state">
          No pudimos cargar el pipeline. Recarga o intenta más tarde.
        </div>
      ) : (
        <Kanban
          stages={stages ?? []}
          leads={leads ?? []}
          focusStageId={search.stage}
          onLeadMove={handleLeadMove}
          onLeadClick={(lead) => setSelectedLead(lead)}
        />
      )}

      <LostReasonDialog
        open={!!pendingLostMove}
        stageName={pendingLostMove?.stageName ?? 'Perdida'}
        initialReason={pendingLostMove?.currentReason ?? ''}
        onCancel={() => setPendingLostMove(null)}
        onConfirm={(reason) => {
          if (!pendingLostMove) return;
          moveLead.mutate({
            leadId: pendingLostMove.leadId,
            stageId: pendingLostMove.stageId,
            position: pendingLostMove.position,
            lost_reason: reason,
          });
          setPendingLostMove(null);
        }}
      />

      <LeadDetailPanel
        lead={selectedLead}
        stages={stages ?? []}
        open={!!selectedLead}
        onOpenChange={(open) => !open && setSelectedLead(null)}
      />
    </div>
  );
}
